import {
  HOST_KEY_HEADER,
  UPLOADS_PATH,
  TOKEN_IMAGES_CATEGORY,
  PILOT_PORTRAITS_CATEGORY,
  MECH_PORTRAITS_CATEGORY,
  tokenImageUploadId,
  newTokenImageVersion,
  WORLD_PACKS_PATH,
  WORLD_SIDECARS_PATH,
  WORLD_SCENES_PATH,
  WORLD_CODEX_PATH,
  WORLD_ENCOUNTERS_PATH,
  type SceneListResponse,
  type ScenePresetPayload,
  type WorldPacksResponse,
  type WorldSidecarsResponse,
  type StaleWorldSidecar,
  type EncounterListResponse,
  type EncounterSummary,
  type CreateEncounterRequest,
  type CreateEncounterResponse,
  type GridMode,
  type UploadResponse,
  type Result,
  ok,
  err,
  errorMessage,
} from "@lancer-vtt/shared";
import type { SidecarFiles } from "@lancer-vtt/rules";
import type { CodexContent } from "@lancer-vtt/domain";

// ── the REST boundary's single `try` ─────────────────────────────────────────────

/**
 * Read the success body of a 2xx response. Runs **inside** {@link request}'s `try`, so a
 * malformed body is channelled the same way a network failure is rather than escaping as a throw.
 */
type DecodeBody<T> = (res: Response) => Promise<T>;

/**
 * The one place in this module that catches. `fetch` throws on a network failure (offline,
 * server unreachable, DNS), and a `res.json()` throws on a malformed body — both are *expected*
 * rejections of a REST call, so they belong on the `Result` channel, not on `throw`
 * (`CODE_STYLE.md`, "`Result`, never `throw`, for an expected rejection").
 *
 * The error string is exactly what a call-site `catch (e) => e.message` used to produce: the
 * server's own `{ error }` when it sent one, else `<failPrefix>: <status>`, else the thrown
 * message.
 */
async function request<T>(args: {
  path: string;
  init?: RequestInit;
  /** Prefixes the fallback message when the error body carries no `error` field. */
  failPrefix: string;
  decode: DecodeBody<T>;
}): Promise<Result<T, string>> {
  try {
    const res = await fetch(args.path, args.init);
    if (!res.ok) {
      const detail = (await res.json().catch(() => undefined)) as { error?: string } | undefined;
      return err(detail?.error ?? `${args.failPrefix}: ${res.status}`);
    }
    return ok(await args.decode(res));
  } catch (e) {
    return err(errorMessage(e));
  }
}

/** One GM-supplied world pack, decoded to raw `.lcp` bytes. */
export type FetchedWorldPack = {
  name: string;
  bytes: Uint8Array;
}

/** Decode a base64 string to bytes in the browser (no Node `Buffer`). */
function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

/**
 * Fetch the GM-supplied content packs the world server holds, decoding each from base64
 * to raw `.lcp` bytes. The caller merges them **transiently** into the live Compendium
 * only — never IndexedDB. An empty array means the world has no GM packs; a server with
 * no world folder also returns an empty list.
 */
export async function fetchWorldPacks(): Promise<FetchedWorldPack[]> {
  const res = await fetch(WORLD_PACKS_PATH);
  if (!res.ok) throw new Error(`world packs fetch failed: ${res.status}`);
  const body = (await res.json()) as WorldPacksResponse;
  return body.packs.map((p) => ({ name: p.name, bytes: base64ToBytes(p.bytesBase64) }));
}

/** One world sidecar pack fetched over {@link WORLD_SIDECARS_PATH}: name + its raw manifest/overlay files. */
export type FetchedWorldSidecar = {
  name: string;
  files: SidecarFiles;
}

/**
 * Fetch the world sidecar packs the server validated at boot, each an already-validated
 * `SidecarFiles` (plain JSON text — no base64 hop, unlike {@link fetchWorldPacks}'
 * opaque `.lcp` bytes). The caller runs the SAME `parseSidecar`/`loadSidecarChain`/`mergeOverlays`
 * pipeline the server ran (`@lancer-vtt/rules`) to rebuild the identical merged action overlay
 * client-side — see `./compendium`'s `useCompendium`. An empty array means the world has no
 * sidecar packs (or none validated); a server with no world folder also returns an empty list.
 */
export async function fetchWorldSidecars(): Promise<FetchedWorldSidecar[]> {
  const res = await fetch(WORLD_SIDECARS_PATH);
  if (!res.ok) throw new Error(`world sidecars fetch failed: ${res.status}`);
  const body = (await res.json()) as WorldSidecarsResponse;
  return body.sidecars.map((s) => ({ name: s.name, files: s.files }));
}

/**
 * The world sidecar copies the server found out of date with the repo's authored packs at boot, each
 * with its restage command (`backend/src/world/sidecarStaleness.ts`). Rides the same response as
 * {@link fetchWorldSidecars}; empty from a server that predates the check or ships no `packs/`.
 */
export async function fetchStaleWorldSidecars(): Promise<StaleWorldSidecar[]> {
  const res = await fetch(WORLD_SIDECARS_PATH);
  if (!res.ok) throw new Error(`world sidecars fetch failed: ${res.status}`);
  return ((await res.json()) as WorldSidecarsResponse).load.stale ?? [];
}

// ── terrain-preset (scene) library ───────────────────────────────────────────────
// REST glue for the GM's preset-library UI on `/map`. List + read are public; save is
// GM-gated by the host key (the same credential the Game-room login checks). The *load*
// into the encounter is NOT here — the caller builds the one undoable `apply_terrain_map`
// command from the fetched preset and dispatches it over the encounter room.

/** List every saved terrain preset's name (empty when there's no world folder). */
export async function listScenes(): Promise<string[]> {
  const res = await fetch(WORLD_SCENES_PATH);
  if (!res.ok) throw new Error(`scene list failed: ${res.status}`);
  return ((await res.json()) as SceneListResponse).scenes;
}

/** Fetch one saved preset by name (its terrain + edges + map radius). */
export async function fetchScene(name: string): Promise<ScenePresetPayload> {
  const res = await fetch(`${WORLD_SCENES_PATH}/${encodeURIComponent(name)}`);
  if (!res.ok) throw new Error(`scene "${name}" fetch failed: ${res.status}`);
  return (await res.json()) as ScenePresetPayload;
}

/**
 * Save a terrain preset (GM-only — the host key authenticates the mutating call, mirroring
 * the server's `gmAuthed` gate). Throws on a non-OK response (e.g. 403 bad host key, 400
 * bad name) so the caller can surface it inline.
 */
export async function saveScene(payload: ScenePresetPayload, hostKey: string): Promise<void> {
  const res = await fetch(`${WORLD_SCENES_PATH}/${encodeURIComponent(payload.name)}`, {
    method: "PUT",
    headers: { "content-type": "application/json", [HOST_KEY_HEADER]: hostKey },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const detail = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(detail?.error ?? `scene save failed: ${res.status}`);
  }
}

// ── encounter library (encounter CRUD) ───────────────────────────────────────────
// REST glue for the GM desktop's ENCOUNTERS row. List is public; create + delete are
// GM-gated by the host key (the same credential the scenes mutations use).

/** Every encounter on the world server (id + name + grid mode); empty when there's none. */
export function listEncounters(): Promise<Result<EncounterSummary[], string>> {
  return request({
    path: WORLD_ENCOUNTERS_PATH,
    failPrefix: "encounter list failed",
    decode: async (res) => ((await res.json()) as EncounterListResponse).encounters,
  });
}

/** Create a new encounter (GM-only — the host key authenticates). Returns the new summary. */
export function createEncounter(
  name: string,
  gridMode: GridMode,
  hostKey: string,
): Promise<Result<EncounterSummary, string>> {
  const body: CreateEncounterRequest = { name, gridMode };
  return request({
    path: WORLD_ENCOUNTERS_PATH,
    init: {
      method: "POST",
      headers: { "content-type": "application/json", [HOST_KEY_HEADER]: hostKey },
      body: JSON.stringify(body),
    },
    failPrefix: "encounter create failed",
    decode: async (res) => ((await res.json()) as CreateEncounterResponse).encounter,
  });
}

// ── pilot profile image upload ───────────────────────────────────────────────────

/**
 * Stamp a freshly-uploaded file's URL with a version token.
 *
 * An upload is stored at a **stable** path — re-uploading for the same pilot/mech overwrites in
 * place — so the URL alone can't distinguish old bytes from new. The server serves uploads with
 * `max-age=86400` (`UPLOAD_CACHE_CONTROL`, `backend/src/uploads.ts`), so without this token a
 * replaced portrait would keep rendering from cache for a day on every client — and replacing a
 * portrait is the feature. The token moves the URL whenever the bytes move, the same trick Vite
 * plays on `/assets/*` with content hashes, so the resource is effectively immutable *per URL*
 * and the background art precache can warm it.
 *
 * **The safety precondition, pinned by `api.test.ts`: nothing may PARSE this string.** The stored
 * value goes into `PilotData.image` / `MechData.image` and is only ever dropped into an `<img
 * src>`; no caller splits it, runs it through `URL()`, or derives a disk path from it (the server
 * derives its own path from the route params, never from the stored string). Adding a query
 * suffix is safe exactly as long as that stays true — if you ever need to parse it, strip the
 * query first.
 *
 * A timestamp rather than a content hash: the client doesn't hash the bytes, and it only needs
 * "different from last time", not global uniqueness.
 */
function versionedUploadUrl(path: string): string {
  return `${path}?v=${Date.now()}`;
}

/**
 * Upload a pilot's profile image to the generic file-upload service (`POST
 * /api/uploads/pilot-portraits/<pilotId>`, `multipart/form-data`, field `file`). Returns the
 * **server-relative URL** the stored file is served back at (`/api/uploads/pilot-portraits/<pilotId>.<ext>`)
 * — persist it onto the pilot via `set_pilot_text` (field `image`) and drop it straight into an
 * `<img src>`. Re-uploading for the same pilot overwrites in place (same category/id). A non-OK
 * response (413 too large / 415 unsupported type / 400 bad request) comes back as `err(message)`
 * so the caller can surface it inline; the server is the real gate — any client-side pre-check is
 * UX only.
 */
export function uploadPilotImage(pilotId: string, file: File): Promise<Result<string, string>> {
  const body = new FormData();
  body.set("file", file);
  return request({
    path: `${UPLOADS_PATH}/${PILOT_PORTRAITS_CATEGORY}/${encodeURIComponent(pilotId)}`,
    init: { method: "POST", body },
    failPrefix: "image upload failed",
    decode: async (res) => versionedUploadUrl(`${UPLOADS_PATH}/${((await res.json()) as UploadResponse).path}`),
  });
}

// ── mech portrait image upload (mirrors pilot portraits above) ───────────────────

/**
 * Upload a mech's portrait image to the generic file-upload service (`POST
 * /api/uploads/mech-portraits/<mechId>`, `multipart/form-data`, field `file`). Returns the
 * **server-relative URL** the stored file is served back at (`/api/uploads/mech-portraits/<mechId>.<ext>`)
 * — persist it onto the mech via `set_mech_image`, then drop it straight into an `<img src>`.
 * Re-uploading for the same mech overwrites in place (same category/id). A non-OK response (413 too
 * large / 415 unsupported type / 400 bad request) comes back as `err(message)` so the caller can
 * surface it inline; the server is the real gate — any client-side pre-check is UX only.
 */
export function uploadMechImage(mechId: string, file: File): Promise<Result<string, string>> {
  const body = new FormData();
  body.set("file", file);
  return request({
    path: `${UPLOADS_PATH}/${MECH_PORTRAITS_CATEGORY}/${encodeURIComponent(mechId)}`,
    init: { method: "POST", body },
    failPrefix: "image upload failed",
    decode: async (res) => versionedUploadUrl(`${UPLOADS_PATH}/${((await res.json()) as UploadResponse).path}`),
  });
}

// ── GM-set board token image upload (mirrors the portraits above) ────────────────

/**
 * Upload a combatant's board token image (`POST /api/uploads/token-images/<id>`, the id from
 * `tokenImageUploadId(encounterId, combatantId, version)`). Returns the server-relative URL to write
 * onto the combatant with `set_token_image`. Each upload gets its own file (a fresh version in the
 * id), so undoing a re-upload restores a URL that still serves the earlier image.
 */
export function uploadTokenImage(args: { encounterId: string; combatantId: string; file: File }): Promise<Result<string, string>> {
  const body = new FormData();
  body.set("file", args.file);
  return request({
    path: `${UPLOADS_PATH}/${TOKEN_IMAGES_CATEGORY}/${tokenImageUploadId(args.encounterId, args.combatantId, newTokenImageVersion())}`,
    init: { method: "POST", body },
    failPrefix: "image upload failed",
    decode: async (res) => versionedUploadUrl(`${UPLOADS_PATH}/${((await res.json()) as UploadResponse).path}`),
  });
}

/** Delete an encounter by id (GM-only — the host key authenticates). */
export function deleteEncounter(id: string, hostKey: string): Promise<Result<void, string>> {
  return request({
    path: `${WORLD_ENCOUNTERS_PATH}/${encodeURIComponent(id)}`,
    init: { method: "DELETE", headers: { [HOST_KEY_HEADER]: hostKey } },
    failPrefix: "encounter delete failed",
    // The 200 body (`{ ok, id }`) carries nothing the caller needs; it is deliberately not read.
    decode: async () => undefined,
  });
}

/**
 * Fetch the world's authored **campaign codex** content (`WORLD_CODEX_PATH`) — the
 * entries, their header metadata and their fact prose, exactly as the server validated
 * them at boot. A world with no `codex.json` answers an empty entry list, which is a
 * legal codex, not an error.
 *
 * **One payload, every recipient** (`decisions/0157`) — the route is unauthenticated and
 * returns the same bytes to everyone; there is no audience split and no reveal state to
 * join against. Content changes only when the GM edits the file and restarts, so a client
 * fetches this once. `cli/src/codex-cli.ts` reads the same route headlessly.
 */
export async function fetchWorldCodex(): Promise<CodexContent> {
  const res = await fetch(WORLD_CODEX_PATH);
  if (!res.ok) throw new Error(`world codex fetch failed: ${res.status}`);
  return (await res.json()) as CodexContent;
}
